<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Timesheets</h1>
            <p class="text-slate-400">Manage employee timesheets, work hours, and payroll tracking</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showCreateTimesheetModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Create Timesheet
            </button>
            <button @click="exportTimesheetData" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export Data
            </button>
          </div>
        </div>
      </div>

      <!-- Timesheet Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Total Hours</p>
            <p class="text-3xl font-bold text-white">{{ timesheetStats.totalHours }}</p>
            <p class="text-sm text-emerald-400">this week</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Active Employees</p>
            <p class="text-3xl font-bold text-white">{{ timesheetStats.activeEmployees }}</p>
            <p class="text-sm text-cyan-400">this week</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Approval</p>
            <p class="text-3xl font-bold text-white">{{ timesheetStats.pendingApproval }}</p>
            <p class="text-sm text-amber-400">timesheets</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Overtime Hours</p>
            <p class="text-3xl font-bold text-white">{{ timesheetStats.overtimeHours }}</p>
            <p class="text-sm text-purple-400">this week</p>
          </div>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-8">
        <h2 class="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button @click="showCreateTimesheetModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-emerald-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            <span class="text-sm font-medium text-white">Create Timesheet</span>
          </button>
          
          <button @click="showScheduledShiftsModal = true" class="quick-action-card">
            <svg class="w-8 h-8 text-cyan-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">View Scheduled Shifts</span>
          </button>
          
          <button @click="approveAllTimesheets" class="quick-action-card">
            <svg class="w-8 h-8 text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-sm font-medium text-white">Approve All</span>
          </button>
          
          <button @click="generateTimesheetReport" class="quick-action-card">
            <svg class="w-8 h-8 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Generate Report</span>
          </button>
        </div>
      </div>

      <!-- Current Week Timesheet -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Weekly Timesheet -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-xl font-semibold text-white">Current Week Timesheet</h2>
              <div class="flex items-center space-x-2">
                <input 
                  v-model="searchTerm" 
                  type="text" 
                  placeholder="Search employees..."
                  class="form-input w-48"
                />
                <select v-model="filterStatus" class="form-select">
                  <option value="">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div class="space-y-4">
              <div v-for="timesheet in filteredTimesheets" :key="timesheet.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-4">
                  <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <span class="text-sm font-bold text-white">{{ timesheet.employee.charAt(0) }}</span>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-white">{{ timesheet.employee }}</h3>
                    <p class="text-xs text-slate-400">{{ timesheet.weekOf }} • {{ timesheet.totalHours }} hours</p>
                  </div>
                </div>
                <div class="flex items-center space-x-4">
                  <div class="text-right">
                    <p class="text-sm font-medium text-white">{{ timesheet.totalHours }}h</p>
                    <p class="text-xs text-slate-400">{{ timesheet.overtimeHours }}h OT</p>
                  </div>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    timesheet.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                    timesheet.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-red-500/20 text-red-400'
                  ]">
                    {{ timesheet.status }}
                  </span>
                  <div class="flex items-center space-x-2">
                    <button @click="viewTimesheetDetails(timesheet)" class="text-emerald-400 hover:text-emerald-300">
                      View
                    </button>
                    <button @click="approveTimesheet(timesheet.id)" class="text-cyan-400 hover:text-cyan-300">
                      Approve
                    </button>
                    <button
                      v-if="timesheet.status === 'pending'"
                      @click="deleteTimesheet(timesheet)"
                      class="text-red-400 hover:text-red-300"
                    >Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Timesheet Summary -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Timesheet Summary</h3>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Total Hours</span>
                <span class="text-sm font-medium text-white">{{ timesheetSummary.totalHours }}h</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Regular Hours</span>
                <span class="text-sm font-medium text-emerald-400">{{ timesheetSummary.regularHours }}h</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Overtime Hours</span>
                <span class="text-sm font-medium text-amber-400">{{ timesheetSummary.overtimeHours }}h</span>
              </div>
              <div class="border-t border-slate-700/50 pt-4">
                <div class="flex items-center justify-between">
                  <span class="text-lg font-semibold text-white">Total Payroll</span>
                  <span class="text-lg font-bold text-emerald-400">${{ timesheetSummary.totalPayroll.toLocaleString() }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Upcoming Shifts -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mt-6">
            <h3 class="text-lg font-semibold text-white mb-4">Upcoming Shifts</h3>
            
            <div class="space-y-3">
              <div v-for="shift in upcomingShifts" :key="shift.id" class="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                <div>
                  <p class="text-sm font-medium text-white">{{ shift.employee }}</p>
                  <p class="text-xs text-slate-400">{{ formatDate(shift.date) }} • {{ shift.startTime }}-{{ shift.endTime }}</p>
                </div>
                <span :class="[
                  'px-2 py-1 rounded-full text-xs font-medium',
                  shift.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                  shift.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                ]">
                  {{ shift.status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Timesheet Modal -->
      <div v-if="showCreateTimesheetModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Create Timesheet Entry</h3>
          
          <form @submit.prevent="submitTimesheet" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Employee</label>
                <select v-model="timesheetData.employee" class="form-select w-full" required>
                  <option value="">Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.name">{{ employee.name }}</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
                <input v-model="timesheetData.date" type="date" class="form-input w-full" required />
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
                <input v-model="timesheetData.startTime" type="time" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">End Time</label>
                <input v-model="timesheetData.endTime" type="time" class="form-input w-full" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Project/Job</label>
              <input v-model="timesheetData.project" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea v-model="timesheetData.description" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showCreateTimesheetModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Create Entry
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Scheduled Shifts Modal -->
      <div v-if="showScheduledShiftsModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-4xl">
          <h3 class="text-xl font-semibold text-white mb-4">Scheduled Shifts</h3>
          
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Employee</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Start Time</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">End Time</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Hours</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="shift in scheduledShifts" :key="shift.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ shift.employee }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(shift.date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ shift.startTime }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ shift.endTime }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ shift.hours }}h</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      shift.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                      shift.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-red-500/20 text-red-400'
                    ]">
                      {{ shift.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="flex items-center justify-end space-x-3 pt-4">
            <button @click="showScheduledShiftsModal = false" class="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const showCreateTimesheetModal = ref(false)
const showScheduledShiftsModal = ref(false)
const searchTerm = ref('')
const filterStatus = ref('')

const timesheetStats = ref({
  totalHours: 0,
  activeEmployees: 0,
  pendingApproval: 0,
  overtimeHours: 0
})

const timesheets = ref([])
const upcomingShifts = ref([])
const scheduledShifts = ref([])
const employees = ref([])

const timesheetData = ref({
  employee: '',
  date: '',
  startTime: '',
  endTime: '',
  project: '',
  description: ''
})

// Computed
const filteredTimesheets = computed(() => {
  let filtered = timesheets.value

  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase()
    filtered = filtered.filter(timesheet => 
      timesheet.employee.toLowerCase().includes(search) ||
      timesheet.project.toLowerCase().includes(search)
    )
  }

  if (filterStatus.value) {
    filtered = filtered.filter(timesheet => timesheet.status === filterStatus.value)
  }

  return filtered
})

const timesheetSummary = computed(() => {
  const totalHours = timesheets.value.reduce((sum, t) => sum + t.totalHours, 0)
  const regularHours = timesheets.value.reduce((sum, t) => sum + Math.min(t.totalHours, 40), 0)
  const overtimeHours = timesheets.value.reduce((sum, t) => sum + Math.max(0, t.totalHours - 40), 0)
  const totalPayroll = totalHours * 25 // Assuming $25/hour average rate
  
  return {
    totalHours,
    regularHours,
    overtimeHours,
    totalPayroll
  }
})

// Methods
const fetchTimesheetStats = async () => {
  try {
    const response = await fetch('/api/company/timesheet-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      timesheetStats.value = data
    }
  } catch (error) {
    console.error('Error fetching timesheet stats:', error)
  }
}

const fetchTimesheets = async () => {
  try {
    const response = await fetch('/api/company/timesheets', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      timesheets.value = data.timesheets || []
    }
  } catch (error) {
    console.error('Error fetching timesheets:', error)
  }
}

const fetchUpcomingShifts = async () => {
  try {
    const response = await fetch('/api/company/upcoming-shifts', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      upcomingShifts.value = data.shifts || []
    }
  } catch (error) {
    console.error('Error fetching upcoming shifts:', error)
  }
}

const fetchScheduledShifts = async () => {
  try {
    const response = await fetch('/api/company/scheduled-shifts', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      scheduledShifts.value = data.shifts || []
    }
  } catch (error) {
    console.error('Error fetching scheduled shifts:', error)
  }
}

const fetchEmployees = async () => {
  try {
    const response = await fetch('/api/company/employees', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      employees.value = data.employees || []
    }
  } catch (error) {
    console.error('Error fetching employees:', error)
  }
}

const submitTimesheet = async () => {
  try {
    const response = await fetch('/api/company/create-timesheet', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(timesheetData.value)
    })
    
    if (response.ok) {
      showCreateTimesheetModal.value = false
      timesheetData.value = { employee: '', date: '', startTime: '', endTime: '', project: '', description: '' }
      await fetchTimesheets()
      await fetchTimesheetStats()
    }
  } catch (error) {
    console.error('Error creating timesheet:', error)
  }
}

const approveTimesheet = async (timesheetId: number) => {
  try {
    const response = await fetch('/api/company/approve-timesheet', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ timesheetId })
    })
    
    if (response.ok) {
      await fetchTimesheets()
      await fetchTimesheetStats()
    }
  } catch (error) {
    console.error('Error approving timesheet:', error)
  }
}

const deleteTimesheet = async (timesheet: any) => {
  if (!window.confirm(`Delete the pending timesheet for ${timesheet.employee}?`)) return
  try {
    const response = await fetch(`/api/company/timesheets/${timesheet.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || 'Delete failed')
    await fetchTimesheets()
    await fetchTimesheetStats()
  } catch (error) {
    console.error('Error deleting timesheet:', error)
  }
}

const approveAllTimesheets = async () => {
  try {
    const response = await fetch('/api/company/approve-all-timesheets', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      await fetchTimesheets()
      await fetchTimesheetStats()
    }
  } catch (error) {
    console.error('Error approving all timesheets:', error)
  }
}

const viewTimesheetDetails = (timesheet: any) => {
  // Navigate to timesheet details page
}

const generateTimesheetReport = () => {
  // Generate timesheet report
}

const exportTimesheetData = () => {
  const csvContent = [
    ['Employee', 'Date', 'Start Time', 'End Time', 'Hours', 'Project', 'Status'],
    ...filteredTimesheets.value.map(timesheet => [
      timesheet.employee,
      formatDate(timesheet.date),
      timesheet.startTime,
      timesheet.endTime,
      timesheet.totalHours,
      timesheet.project,
      timesheet.status
    ])
  ].map(row => row.join(',')).join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'company-timesheets.csv'
  a.click()
  window.URL.revokeObjectURL(url)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchTimesheetStats(),
    fetchTimesheets(),
    fetchUpcomingShifts(),
    fetchScheduledShifts(),
    fetchEmployees()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.quick-action-card {
  @apply flex flex-col items-center justify-center p-4 bg-slate-700/30 rounded-xl hover:bg-slate-600/50 transition-colors;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
